import { ModuleClass, SimutechModule } from './ModuleTypes'

export enum SolverCommand {
    JoinGroup = 'JoinGroup',
    LeaveGroup = 'LeaveGroup',
    ProcessMessage = 'ProcessMessage',
    LoadScene = 'LoadScene',
    LoadFault = 'LoadFault',
    ExitFault = 'ExitFault',
    BeginFault = 'BeginFault',
    CompleteFault = 'CompleteFault',
    ReplaceItem = 'ReplaceItem',
    VerifyDeadPowerSupply = 'VerifyDeadPowerSupply'
}
export enum FaultMode {
    Explore = 'Explore',
    Troubleshoot = 'Troubleshoot',
}
export enum SceneRoom {
    Default = 'Default',
    Hallway = 'Hallway',
    Lab = 'Lab',
    LabOverview = 'LabOverview'
}
export interface AppConfig {
    build: string
    commitID: string
    environmentName: string
    isDebug: string
    CanSelectFault: string
    helpURL: string
    electricalCode: {
        development: string
        production: string
        test: string
    }
    solver: {
        development: {
            URI: string
        }
        production: {
            URI: string
        }
        test: {
            URI: string
        }
    }
    RXD: {
        development: {
            rootURL: string
            LMSRequired: string

        }
        production: {
            rootURL: string
            LMSRequired: string
        }
        test: {
            rootURL: string
            LMSRequired: string
        }
    }
    XAPI: {
        development: {
            learningEngineURL: string
        }
        production: {
            learningEngineURL: string
        }
        test: {
            learningEngineURL: string
        }
    }
}

export enum ElectricalCode {
    NEC = 'NEC',
    IEC = 'IEC'
}

export type ModuleNavItem = {
    name: string
    path: string
}
export interface SimulationModule {
    type: ModuleClass
    solverModule: SimutechModule
    moduleHelpURL: string
    company: string
    unityLoader: string
    unityConfig: string
    userID: string
    userName: string
    navItems: ModuleNavItem[]
    toolboxItems: ToolboxItem[]
    ldlInfos: LDLItem[]
    diagrams: ResourceImage[]
    faultModeMessage?: FaultModeMessage
    faultMode?: FaultMode
    isLab: boolean
    isTS: boolean
}

export interface User {
    userId: string
    userName: string
    companyId: string
    companyName: string
    moduleId: string
    electricalCode: ElectricalCode

}

export interface ResourceImage {
    name: string
    filename: string
}

export interface FaultModeMessage {
    explore: string
    troubleshoot: string
}

export enum ToolName {
    LockerOpen = 'locker-open',
    LockerLocked = 'locker-locked',
    Folder = 'folder',
    Screwdrive = 'screwdriver',
    Multimeter = 'multimeter',
}
export interface ToolboxItem {
    name: ToolName
    unityObject?: string
    action?: string
}

export interface LDLItem {
    labs: string[]
    title: string
    tableHeaders: string[]
    steps: any[]
}

export interface Part {
    partID: string
    partType: string
    replaceCost: number
    replaceTime: number
    specs: string
    attributes: any
    partSolverID: string
    actions: {
        '1': PartAction
        '6': PartAction
    }
}
export type PartActions = Part['actions']

export enum PartAction {
    Replace = 'Replace',
    ViewSpecs = 'View Specs'
}

export enum SafetyException {
    Zap = 'Zap',
    Stop = 'Stop',
    Caution = 'Caution',
    SafetyTip = 'SafetyTip',
    MeterTip = 'MeterTip',
    ScrewdriverTip = 'ScrewdriverTip',
    Warning = 'Warning'
}

export enum SoundEffect {
    Cash = 'PlayCashSoundEffect',
    Zap = 'Zap',
    Message = 'Message'
}

// Learning Lab Types

export enum LMSLessonStatus {
    Passed = 1,
    Completed = 2,
    Failed = 3,
    Incomplete = 4,
    Browsed = 5,
    NotAttempted = 6
}

export interface ModuleContent {
    id: number
    title: string
    description: string
    sections: Section[]
}

export enum Availability {
    Disabled = 'section-under-construction'
}
export interface Section {
    id: number
    moduleID: string
    LRSID: string
    name: string
    description: string
    lessons: Lesson[]
}

export interface Lesson {
    id: number
    LRSID: string
    moduleID?: string
    faultID?: string
    benchID?: string
    name: string
    panels: LessonPanel[]
}

export interface LessonPanel {
    id: number
    title: string
    text: string
    image?: PanelImage
    video?: PanelVideo
    quiz?: Quiz
}

export interface PanelImage {
    title?: string
    text?: string
    filename?: string
}

export interface PanelVideo {
    title?: string
    text?: string
    filename?: string
}

export interface Quiz {
    type?: QuizAnswerType
    text?: string
    choices?: QuizChoice[]
    feedback?: {
        correct: string
        incomplete: string
    }
}

export interface QuizChoice {
    id: number
    text: string
    isCorrect: boolean
    feedback: {
        correct: string
        incorrect: string
    }
}

export enum QuizAnswerType {
    MultipleChoice = 'multiple-choice',
    SingleChoice = 'single-choice',
}

export interface WorkOrder {
    Name: string
    StatusOfRepair: string
    ReportedProblem: string
    Comment: string
}

export interface LRSStatement {
    user: User
    statement: Statement
}

export interface Statement {
    'registration-id': string
    action: string
    learningGUID: string
    duration?: string
    sessionInfo?: SessionInfo
}

export interface SessionInfo {
    environment: string
    'build-number': string
    'commit-number': string
    'user-agent': string
    browser: string
    'browser-version': string
    os: string
    version: string
    'screen-size': string
    'browser-window-size': string

}

export interface FaultAttempt {
    skillRating: number
    efficiencyScore: number
    efficiencyRating: number
    accuracyScore: number
    accuracyRating: number
    safetyScore: number
    safetyRating: number
    baseScore: number
    overallScore: number
    faultFixed: boolean
    faultOperational: boolean
    efficiencyPenalties: EfficiencyPenalty
    accuracyPenalties: AccuracyPenalty
    safetyPenalties: SafetyPenalty
    performanceSummary: PerformanceSummary
}

export interface EfficiencyPenalty {
    seatTimeBonusScore: number
    seatTimePenaltyScore: number
    meterReadingsPenaltyScore: number
    enclosurePenaltyScore: number
    inspectionPenaltyScore: number
    benchTestPenaltyScore: number
    meterTipPenaltyScore: number
    hintPenaltyScore: number
    lockoutsPenaltyScore: number
}

export interface AccuracyPenalty {
    numberUnnecessaryReplacedComponents: number
    numberUnnecessaryRepairedComponents: number
    numberUnnecessaryAdjustments: number
    numberUnnecessaryCalibrations: number
    allowedReplacementPenaltyScore: number
    allowedFusePenaltyScore: number
    allowedPLCDownloadsPenaltyScore: number
    allowedPLCSettingsPenaltyScore: number
    allowedRepairsPenaltyScore: number
    allowedAdjustmentsPenaltyScore: number
    allowedCalibrationsPenaltyScore: number
}

export interface SafetyPenalty {
    stopPenalties: number
    cautionPenalties: number
    safetyTipPenalties: number
    warningPenalties: number
}

export interface PerformanceSummary {
    seatTimeMilliseconds: number
    seatTimeMinutes: number
    voltmeterReadingsTaken: number
    ohmmeterReadingsTaken: number
    ammeterReadingsTaken: number
    totalMeterReadings: number
    enclosuresOpened: number
    lockoutsPerformed: number
    inspectionsPerformed: number
    benchTestsPerformed: number
    meterTipsIssued: number
    hintsTaken: number
    componentsReplaced: ReplacedComponent[]
    componentsRepaired: string[]
    componentsAdjusted: string[]
    componentsCalibrated: string[]
    fusesReplaced: number
    PLCProgramsDownloaded: number
    PLCProgramSettingChanges: number
    stopLiveCircuitErrorOccurrences: number
    cautionSafeWorkplacePracticeOccurrences: number
    safetyTipMessagesReceived: number
    warningMessagesReceived: number
    PLCNotInRunModeLeaveAttempt: boolean
    RepairedNotOperationalLeaveAttempt: boolean
    exitWithoutSolvingFault: boolean
    exitWithoutCircuitOperational: boolean
    exitByZapPersonalInjuryError: boolean
    exitByProcedureError: boolean
    totalTimeMinutes: number
    totalCost: number
}

export interface ReplacedComponent {
    Name: string
    PartType: string
    Cost: number
    Time: number
}

export type MasteryLevelName = 'Basic' | 'Intermediate' | 'Advanced' | 'Genius' | 'Master'

export type MasteryLevel = {
    level: number
    name: MasteryLevelName
}

export const masteryLevels: MasteryLevel[] = [
    { level: 0, name: 'Basic' },
    { level: 1, name: 'Intermediate' },
    { level: 2, name: 'Advanced' },
    { level: 3, name: 'Genius' },
    { level: 4, name: 'Master' },
]

export enum FaultState {
    Repaired = 'Repaired',
    NotRepaired = 'NotRepaired',
    Abandoned = 'Abandoned'
}
export type FaultHistoryItem = {
    id: string
    date: string
    startTime: string
    faultId: string
    faultState: FaultState
    skillRating: string
}
export type FullFaultAttempt = {
    _id: string
    faultId: string
    timestamp: string
    faultState: FaultState
    scoringResults: ScoringResults
    performanceMetrics: PerformanceMetrics
}

export type ScoringResults = {
    'overall-score': number
    'skill-rating': number
    'safety-rating': number
    'accuracy-rating': number
    'efficiency-rating': number
    'seat-time-bonus-score': number
    'seat-time-penalty-score': number
    'meter-readings-penalty-score': number
    'enclosure-penalty-score': number
    'inspection-penalty-score': number
    'bench-test-penalty-score': number
    'meter-tip-penalty-score': number
    'hint-penalty-score': number
    'lockouts-penalty-score': number
    'allowed-replacement-penalty-score': number
    'allowed-fuse-penalty-score': number
    'allowed-plc-downloads-penalty-score': number
    'allowed-plc-settings-penalty-score': number
    'allowed-repairs-penalty-score': number
    'allowed-adjustments-penalty-score': number
    'allowed-calibrations-penalty-score': number
    'stop-penalties-score': number
    'caution-penalties-score': number
    'safety-tip-penalties-score': number
    'warning-penalties-score': number
    DefectType: string
    Defect: string
}

export type PerformanceMetrics = {
    RusticiCCAccountID: string
    RusticiCCAccountName: string
    LearningSessionID: string
    faultAttemptID: string
    ModuleIDLong: string
    ModuleIDShort: string
    ModuleFaultID: string
    'seat-time': number
    'voltmeter-readings': number
    'ohmmeter-readings': number
    'ammeter-readings': number
    lockouts: number
    'meter-tips-received': number
    'hints-taken': number
    'replaced-components': ReplacedComponent[]
    repairs: any[]
    enclosures: string[]
    inspections: number
    'bench-tests': number
    adjustments: any[]
    calibrations: any[]
    'replaced-fuses': number
    'plc-program-downloads': number
    'plc-program-setting-changes': number
    'tightened-connections': string[]
    'stop-live-circuit-error-occurrences': number
    'caution-safe-workplace-practice-occurrences': number
    'safety-tip-messages-received': number
    'warning-messages-received': number
    'exit-without-solving-fault': boolean
    'exit-by-zap-personal-injury-error': boolean
    'attempt-to-leave-fault-with-plc-not-in-run-mode': boolean
    'attempt-to-leave-fault-with-fault-repaired-but-circuit-not-operational': boolean
    'exit-without-circuit-being-operational': boolean
    'exit-by-procedure-failure': boolean
    DefectType: string
    Defect: string
}

type ScoreboardDataPoint = {
    name: string
    description: string
    value: number
}

export type ScoreboardData = {
    overallScore: ScoreboardDataPoint
    levelAchieved: ScoreboardDataPoint
    faultsAttempted: number
    faultsSolved: number
    faultPercentage: number
    accuracyScore: ScoreboardDataPoint
    efficiencyScore: ScoreboardDataPoint
    safetyScore: ScoreboardDataPoint
    skillRating: ScoreboardDataPoint
}